import i18n from '@/utils/i18n';
import React, { useMemo } from 'react';
import {
  getParamsFromNameValue,
  transformSaltItemParams,
} from '@/utils/salt-item';
import { ItemContext_SaltItemFieldsFragment } from '@/types';
import { RichInfoSection } from './rich-info-section';
import { RichInfoTable } from './rich-info-table';
import { StyleSheet } from 'react-native';

interface Props {
  horizontal?: boolean;
  saltItemInfo: ItemContext_SaltItemFieldsFragment;
}

export const RichInfoSaltProductSpecs: React.FC<Props> = ({
  saltItemInfo,
  horizontal,
}) => {
  const {
    basicInformation,
    brandInformation,
    categories,
    packagingAndStorage,
    dietaryInformation,
    servingAndPreparation,
  } = saltItemInfo;

  const {
    productTableData,
    shippingTableData,
    servingAndPreparationTableData,
  } = useMemo(() => {
    const basicInformationParams = {
      ...getParamsFromNameValue(basicInformation.additionalInformation),
      ...getParamsFromNameValue(basicInformation.names),
    };

    const brandInformationParams = {
      ...getParamsFromNameValue(brandInformation.additionalInformation),
    };

    const packagingAndStorageParams = {
      ...getParamsFromNameValue(
        packagingAndStorage.packaging.additionalInformation,
      ),
      ...getParamsFromNameValue(packagingAndStorage.storage),
    };

    const dietaryInformationParams = {
      ...getParamsFromNameValue(dietaryInformation.additionalInformation),
      ...getParamsFromNameValue(dietaryInformation.dietTypes),
    };

    const servingAndPreparationParams = {
      ...getParamsFromNameValue(servingAndPreparation.serving),
      ...getParamsFromNameValue(servingAndPreparation.preparation),
    };

    const category = categories.reduce((acc, c) => {
      if (c.name && c.type === 'Category') {
        return acc ? `${acc}, ${c.name}` : c.name;
      }
      return acc;
    }, '');

    // display only GTIN_METADATA SOURCE_FIELDs, sorted in the same order as in the GTIN
    const productInformation = {
      /* eslint-disable sort-keys/sort-keys-fix */
      short_name: basicInformationParams['Short Name'] ?? null,
      brand_name: brandInformation.brandName ?? null,
      manufacturer: brandInformation.manufacturer ?? null,
      category: category ? category : null,
      manufacturer_product_code:
        basicInformationParams.MPN ??
        basicInformationParams.manufacturer_product_code ??
        null,
      gtin:
        basicInformationParams.GTIN ?? basicInformationParams.item_id ?? null,
      pack_unit_quantity: packagingAndStorageParams.pack_unit_quantity ?? null,
      pack_size: packagingAndStorageParams.pack_size ?? null,
      gross_weight: packagingAndStorage.packaging.grossWeight ?? null,
      net_weight: packagingAndStorage.packaging.netWeight ?? null,
      origin_country: brandInformationParams.origin_country ?? null,
      kosher: dietaryInformationParams.kosher_flag ?? null,
      child_nutrition: dietaryInformationParams.child_nutrition_flag ?? null,
      /* eslint-enable sort-keys/sort-keys-fix */
    };

    const shippingInformation = {
      /* eslint-disable sort-keys/sort-keys-fix */
      ship_length: packagingAndStorage.packaging.packLength ?? null,
      ship_width: packagingAndStorage.packaging.packWidth ?? null,
      ship_height: packagingAndStorage.packaging.packHeight ?? null,
      ship_volume: packagingAndStorageParams.ship_volume ?? null,
      ship_ti_hi: packagingAndStorageParams.ship_ti_hi ?? null,
      shelf_life_days: packagingAndStorageParams.shelf_life_days ?? null,
      storage_temperature:
        packagingAndStorageParams.storage_temperature ?? null,
      /* eslint-enable sort-keys/sort-keys-fix */
    };

    const servingAndPreparationInformation = {
      preparation_cooking_suggestions:
        servingAndPreparationParams.preparation_cooking_suggestions ?? null,
      serving_suggestion:
        servingAndPreparationParams.serving_suggestion ?? null,
    };

    return {
      productTableData: transformSaltItemParams(productInformation),
      servingAndPreparationTableData: transformSaltItemParams(
        servingAndPreparationInformation,
      ),
      shippingTableData: transformSaltItemParams(shippingInformation),
    };
  }, [
    basicInformation,
    brandInformation,
    categories,
    packagingAndStorage,
    dietaryInformation,
    servingAndPreparation,
  ]);

  if (
    productTableData.length === 0 &&
    shippingTableData.length === 0 &&
    servingAndPreparationTableData.length === 0
  ) {
    return null;
  }

  return (
    <>
      {productTableData.length ? (
        <RichInfoSection
          horizontal={horizontal}
          title={i18n.t('product_specs')}
        >
          <RichInfoTable
            data={productTableData}
            style={productTableData.length ? styles.tableMargin : null}
          />
        </RichInfoSection>
      ) : null}
      {shippingTableData.length ? (
        <RichInfoSection
          horizontal={horizontal}
          title={i18n.t('shipping_information')}
        >
          <RichInfoTable data={shippingTableData} />
        </RichInfoSection>
      ) : null}
      {servingAndPreparationTableData.length ? (
        <RichInfoSection
          horizontal={horizontal}
          title={i18n.t('serving_and_preparation_information')}
        >
          <RichInfoTable data={servingAndPreparationTableData} />
        </RichInfoSection>
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  tableMargin: {
    marginBottom: 12,
  },
});
